import { Group, Badge, Button, Text } from '@repletoryreach/sdk-ui';
import { DataTable, StatusBadge, type StatusTone } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate, formatRelative } from '../../../../lib/format';
import { CopyableId } from '../../../../components/CopyableId';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import { Blank, Money, OrgCell } from '../../components/cells';
import { RowMenu } from '../../../../components/RowMenu';
import type { Indexed } from '../../../../lib/types';
import { useT, type Translate } from '../../../../i18n/I18nProvider';

export function statusTone(s: admin.InvoiceStatus): StatusTone {
  switch (s) {
    case 'paid': return 'healthy';
    case 'issued': return 'pending';
    case 'past_due': return 'warning';
    case 'void': return 'down';
    case 'draft':
    default: return 'inactive';
  }
}

export function statusLabel(t: Translate, s: string): string {
  return t(`billing.invoices.status.${s}`, s);
}

/**
 * The tax verdict SARS or FIRS returned for the invoice. `invalid` means the
 * document does not satisfy the VAT rules of the country it was issued in,
 * which is the one thing this page could not previously tell an operator.
 */
export function validationTone(s: admin.AdminInvoice['validation_status']): StatusTone {
  switch (s) {
    case 'valid': return 'healthy';
    case 'invalid': return 'down';
    case 'skipped': return 'inactive';
    case 'unvalidated':
    default: return 'pending';
  }
}

export function validationLabel(t: Translate, s: string): string {
  return t(`billing.invoices.validation.${s}`, s);
}

export interface InvoiceRowActions {
  /** Open the PDF in the file viewer, which carries Download and Print. */
  onView: (r: admin.AdminInvoice) => void;
  onValidate: (r: admin.AdminInvoice) => void;
  onRegenerate: (r: admin.AdminInvoice) => void;
}

interface Props extends InvoiceRowActions {
  records: admin.AdminInvoice[];
  loading: boolean;
}

export function InvoicesTable({ records, loading, onView, onValidate, onRegenerate }: Props) {
  const t = useT();
  return (
    <DataTable<Indexed<admin.AdminInvoice>>
      records={records as Indexed<admin.AdminInvoice>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('billing.invoices.emptyRows')}
      columns={[
        {
          accessor: 'invoice_number',
          title: t('billing.invoices.columns.number'),
          sortable: true,
          // An invoice number is quoted by customers and support alike, so it
          // is one of the few identifiers that belongs on screen.
          render: (r) => <CopyableId value={r.invoice_number} label={t('billing.invoices.copyNumber')} chars={24} />
        },
        {
          accessor: 'organisation_id',
          title: t('billing.invoices.columns.org'),
          render: (r) => <OrgCell id={r.organisation_id} />
        },
        {
          accessor: 'period_start',
          title: t('billing.invoices.columns.period'),
          sortable: true,
          render: (r) => (
            <TwoLineCell
              primary={t('billing.invoices.period', { from: formatDate(r.period_start), to: formatDate(r.period_end) })}
              secondary={r.pdf_ready
                ? t('billing.invoices.pdf.rendered', { when: r.pdf_generated_at ? formatDate(r.pdf_generated_at) : '' })
                : t('billing.invoices.pdf.notYet')}
            />
          )
        },
        {
          accessor: 'issued_at',
          title: t('billing.invoices.columns.issue'),
          sortable: true,
          render: (r) => r.issued_at ? <span title={r.issued_at}>{formatDate(r.issued_at)}</span> : <Blank />
        },
        {
          accessor: 'due_at',
          title: t('billing.invoices.columns.due'),
          sortable: true,
          render: (r) => {
            if (!r.due_at) return <Blank />;
            // A past-due badge with no date beside it says nothing an operator
            // can act on, so the overdue rows carry how late they are.
            return r.status === 'past_due'
              ? <span title={r.due_at}><TwoLineCell primary={formatDate(r.due_at)} secondary={<Text role="caption" c="var(--rr-color-error)">{formatRelative(r.due_at)}</Text>} /></span>
              : <span title={r.due_at}>{formatDate(r.due_at)}</span>;
          }
        },
        {
          accessor: 'total_minor',
          title: t('billing.invoices.columns.totalInclVat'),
          textAlign: 'right',
          sortable: true,
          // An invoice total is what the tenant owes, not a movement, so it
          // carries no debit or credit colour.
          render: (r) => <Money minor={r.total_minor} currency={r.currency} />
        },
        {
          accessor: 'currency',
          title: t('common.currency'),
          render: (r) => <Text role="bodySmall" c="var(--rr-color-text-muted)">{r.currency}</Text>
        },
        {
          accessor: 'status',
          title: t('common.status'),
          sortable: true,
          render: (r) => <StatusBadge status={statusTone(r.status)}>{statusLabel(t, r.status)}</StatusBadge>
        },
        {
          accessor: 'validation_status',
          title: t('billing.invoices.columns.validation'),
          sortable: true,
          render: (r) => (
            <Group gap="xs" wrap="nowrap">
              <StatusBadge status={validationTone(r.validation_status)}>
                {validationLabel(t, r.validation_status)}
              </StatusBadge>
              {r.validation_validator && (
                <Badge size="xs" variant="light">{r.validation_validator.toUpperCase()}</Badge>
              )}
            </Group>
          )
        },
        {
          accessor: 'actions',
          title: t('common.actions'),
          textAlign: 'right',
          render: (r) => (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              <Button size="xs" variant="default" onClick={() => onView(r)}>
                {t('action.view')}
              </Button>
              <RowMenu
                actions={[
                  { id: 'validate', label: t('billing.invoices.actions.validate'), onSelect: () => onValidate(r) },
                  // Regenerating deletes the cached PDF in object storage, so it
                  // reads as the destructive entry it is.
                  { id: 'regenerate', label: t('billing.invoices.actions.regenerate'), onSelect: () => onRegenerate(r), destructive: true }
                ]}
              />
            </Group>
          )
        }
      ]}
    />
  );
}
